import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSponsorsFindOneQuery, useSponsorsUpdate, useSponsorsUploadContactAvatar, useSponsorConversationLogsQuery } from "@/lib/api/requests/sponsors.requests";
import { useSponsorAgreementsQuery } from "@/lib/api/requests/sponsor-agreements.requests";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { usePageState } from "@/lib/hooks/use-page-state";
import { PageInfoBanner } from "@/util-components/page-layout/page-info-banner";
import { SectionHeader } from "@/util-components/page-layout";
import {
  Loader2,
  Pencil,
  MoreHorizontal,
  Plus,
  MapPin,
  Globe,
  Handshake,
  Activity,
  Users,
  History,
  Euro,
  Files,
  Info,
  MessageSquare,
  User,
  Camera,
  Trash2,
} from "lucide-react";
import { Button } from "@/ui-components/ui/button";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { EditSponsorDialog } from "@/page-components/sponsors/components/EditSponsorDialog";
import { AddContactDialog } from "@/page-components/sponsors/components/AddContactDialog";
import { EditContactDialog } from "@/page-components/sponsors/components/EditContactDialog";
import { SponsorAgreementsSection } from "./components/SponsorAgreementsSection";
import { SponsorConversationLogSection } from "./components/SponsorConversationLogSection";
import { Card, CardContent, CardHeader } from "@/ui-components/ui/card";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/util-components/dropdown-menu";
import {SponsorAgreementResponseDto, AgreementStatus} from "@/lib/api/interfaces/sponsor-agreements.interface";
import { cn } from "@/lib/utils/cn";
import { CreateSponsorAgreementDialog } from "./components/CreateSponsorAgreementDialog";
import { SponsorConversationLogDialog } from "./components/SponsorConversationLogDialog";

const SponsorDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const sponsorId = params.id ?? "";
  const [agreementStatusFilter, setAgreementStatusFilter] = useState<AgreementStatus>(AgreementStatus.PENDING);
  const [isCreateAgreementDialogOpen, setIsCreateAgreementDialogOpen] = useState(false);
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [isEditSponsorDialogOpen, setIsEditSponsorDialogOpen] = useState(false);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);

  const sponsorQuery = useSponsorsFindOneQuery(sponsorId, { enabled: !!sponsorId });
  const agreementsQuery = useSponsorAgreementsQuery(sponsorId, { enabled: !!sponsorId });
  const logsQuery = useSponsorConversationLogsQuery(sponsorId, { enabled: !!sponsorId });

  const updateMutation = useSponsorsUpdate();
  const uploadContactAvatarMutation = useSponsorsUploadContactAvatar();

  const { state: pageState, setInfoBannerVisible } = usePageState("sponsor-details-social-media-hint", {
    isInfoBannerVisible: true,
  });

  // Helper: safely get contacts array
  const contacts = Array.isArray(sponsorQuery.data?.contacts) ? sponsorQuery.data!.contacts : [];

  useEffect(() => {
    if (sponsorQuery.data) {
      // Placeholder for future form mapping
    }
  }, [sponsorQuery.data]);

  const handleContactDelete = async (index: number) => {
    const current = contacts;
    if (index < 0 || index >= current.length) return;
    const next = current
      .filter((_, i) => i !== index)
      .map((c) => ({
        name: c.name,
        email: c.email,
        phone: c.phone,
        mobile: c.mobile,
        avatar: (c as any).avatar ?? null,
        role: (c as any).role ?? null,
      }));
    try {
      await updateMutation.mutateAsync({ id: sponsorId, data: { contacts: next } as any });
      await sponsorQuery.refetch();
    } catch {}
  };

  if (!sponsorId) {
    return <div className="p-4 text-sm text-red-600">Keine Sponsor-ID in der URL gefunden!</div>;
  }

  if (sponsorQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (sponsorQuery.isError || !sponsorQuery.data) {
    return <div className="p-4 text-sm text-red-600">Sponsor konnte nicht geladen werden.</div>;
  }

  const sponsor = sponsorQuery.data;

  const addressLine = (() => {
    const street = sponsor.street?.trim();
    const streetNumber = sponsor.streetNumber?.trim();
    const zip = sponsor.zip?.trim();
    const city = sponsor.city?.trim();
    const country = sponsor.country?.trim();
    const streetPart = [street, streetNumber].filter(Boolean).join(" ").trim();
    const cityPart = [zip, city].filter(Boolean).join(" ").trim();
    const parts: string[] = [];
    if (streetPart) parts.push(streetPart);
    if (cityPart) parts.push(cityPart);
    if (country) parts.push(country);
    return parts.join(", ");
  })();

  const person = sponsor.assignedUser?.value ?? 'Niemandem zugewiesen';

  const websiteHref = sponsor.website
    ? sponsor.website.startsWith("http://") || sponsor.website.startsWith("https://")
      ? sponsor.website
      : `https://${sponsor.website}`
    : undefined;
  const activeLogoUrl = sponsor.logoState?.active?.url || sponsor.logoUrl;

  // Agreements normalisieren
  const agreements = Array.isArray(agreementsQuery.data) ? (agreementsQuery.data as any[]) : [];
  const agreementsCount = agreements.length;
  const now = new Date();
  const activeAgreementsCount = agreements.filter((a: any) => {
    const s = a.startDate ? new Date(a.startDate) : undefined;
    const e = a.endDate ? new Date(a.endDate) : undefined;
    if (s && e) return s <= now && now <= e;
    if (s && !e) return s <= now; // läuft ab Start weiter
    if (!s && e) return now <= e; // endet in Zukunft
    return false;
  }).length;
  const totalVolume = agreements.reduce((sum: number, a: SponsorAgreementResponseDto) => {
    if(a.status !== 'accepted') return sum;
    if(!a.volume) return sum;
    return sum +  a.volume;
  }, 0);

  const potentialVolume = agreements.reduce((sum: number, a: SponsorAgreementResponseDto) => {
    if(a.status !== 'pending') return sum;
    if(!a.volume) return sum;
    return sum +  a.volume;
  }, 0);

  const totalDocuments = agreements.reduce(
    (sum: number, a: any) => sum + (Array.isArray(a.documents) ? a.documents.length : 0),
    0
  );

  // Logs für letzte Aktivität
  const logs = Array.isArray(logsQuery.data) ? (logsQuery.data as any[]) : [];
  const lastActivityAt = (() => {
    const dates = logs
      .map((l) => l.conversationAt || l.createdAt)
      .filter(Boolean)
      .map((d: string) => new Date(d).getTime());
    if (dates.length === 0) return undefined;
    return new Date(Math.max(...dates));
  })();
  const formatRelative = (date?: Date) => {
    if (!date) return "—";
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `vor ${diffMins} Min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `vor ${diffHours} Std`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `vor ${diffDays} Tg`;
    return date.toLocaleDateString("de-DE");
  };

  return (
    <SubPageLayout
      pageTitle={sponsor.name}
      parentPageTitle="Sponsoren"
      parentPageCallback={() => navigate("/sponsoring/sponsoren")}
      navigateBackCallback={() => navigate(-1)}
      icon={<Handshake className="h-4 w-4" />}
      className="bg-gray-50"
      trailing={
        <>
          <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditSponsorDialogOpen(true)}
              className="h-9 rounded-xl border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium transition-all shadow-sm flex items-center gap-2 px-4"
            >
              <Pencil className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Bearbeiten</span>
            </Button>
          </PermissionFeatureWrapper>
          <EditSponsorDialog
            sponsor={sponsor}
            open={isEditSponsorDialogOpen}
            onOpenChange={setIsEditSponsorDialogOpen}
            onSuccess={() => sponsorQuery.refetch()}
          />
        </>
      }
    >
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {pageState.isInfoBannerVisible && (
          <PageInfoBanner
            title="Tipp: Sponsor in Posts einbinden"
            description="Damit dieser Sponsor in Ihren Social-Media-Posts berücksichtigt werden kann, müssen Sie ihm eine aktive Vereinbarung mit einer Leistung zuweisen, die die Erwähnung auf Social Media erlaubt."
            icon={Info}
            variant="purple"
            onClose={() => setInfoBannerVisible(false)}
          />
        )}

        {/* Kopfbereich / Info Card */}
        <Card className="border-none shadow-sm rounded-2xl overflow-hidden bg-white">
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-12">
              {/* Logo Bereich */}
              <div className="md:col-span-4 lg:col-span-3 bg-gray-50/50 border-b md:border-b-0 md:border-r border-gray-100 flex items-center justify-center p-8 min-h-[200px]">
                <div className="relative w-full aspect-square max-w-[180px] flex items-center justify-center">
                  {activeLogoUrl ? (
                    <img 
                      src={activeLogoUrl} 
                      alt={sponsor.name} 
                      className="max-h-full max-w-full object-contain drop-shadow-sm" 
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center border border-gray-200/50">
                        <Handshake className="w-8 h-8 opacity-20" />
                      </div>
                      <span className="text-xs font-medium">Kein Logo</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Info Bereich */}
              <div className="md:col-span-8 lg:col-span-9 p-6 sm:p-8">
                <div className="space-y-6">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{sponsor.name}</h1>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {addressLine ? (
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-50/50 border border-blue-100/50 px-3 py-1 text-xs font-medium text-blue-700">
                          <MapPin className="h-3.5 w-3.5" />
                          {addressLine}
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                          <MapPin className="h-3.5 w-3.5" />
                          Adresse fehlt
                        </div>
                      )}
                      
                      {websiteHref ? (
                        <a
                          href={websiteHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 transition-colors"
                        >
                          <Globe className="h-3.5 w-3.5" />
                          {sponsor.website}
                        </a>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                          <Globe className="h-3.5 w-3.5" />
                          Website fehlt
                        </div>
                      )}
                    </div>
                  </div>

                  {/* KPI Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 pt-4 border-t border-gray-100">
                    <KpiItem 
                      label="Zuständig" 
                      value={person} 
                      icon={User} 
                    />
                    <KpiItem 
                      label="Vereinbarungen" 
                      value={agreementsCount} 
                      icon={Handshake} 
                      highlight={agreementsCount > 0}
                    />
                    <KpiItem 
                      label="Aktiv" 
                      value={activeAgreementsCount} 
                      icon={Activity} 
                      highlight={activeAgreementsCount > 0}
                      variant="success"
                    />
                    <KpiItem 
                      label="Kontakte" 
                      value={contacts.length} 
                      icon={Users} 
                    />
                    <KpiItem 
                      label="Letzte Aktivität" 
                      value={logsQuery.isLoading ? "..." : formatRelative(lastActivityAt)} 
                      icon={History} 
                    />
                    {totalVolume > 0 && (
                      <KpiItem 
                        label="Einnahmen" 
                        value={`${new Intl.NumberFormat("de-DE").format(totalVolume)}€`} 
                        icon={Euro} 
                        variant="success"
                      />
                    )}
                    {potentialVolume > 0 && (
                      <KpiItem 
                        label="Potenzial" 
                        value={`${new Intl.NumberFormat("de-DE").format(potentialVolume)}€`} 
                        icon={Euro} 
                        variant="warning"
                      />
                    )}
                    {totalDocuments > 0 && (
                      <KpiItem 
                        label="Dokumente" 
                        value={totalDocuments} 
                        icon={Files} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hauptinhalte: Vereinbarungen + Protokoll */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <Card className="border-none shadow-sm rounded-2xl overflow-hidden bg-white flex flex-col h-full">
            <CardHeader className="px-6 py-4">
              <SectionHeader 
                icon={Handshake} 
                title="Vereinbarungen" 
                description="Laufende und geplante Sponsoring-Verträge."
                rightElement={
                  <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                    <Button
                      onClick={() => setIsCreateAgreementDialogOpen(true)}
                      size="sm"
                      className="rounded-xl h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all font-bold flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Neu</span>
                    </Button>
                  </PermissionFeatureWrapper>
                }
              />
            </CardHeader>
            <CardContent className="px-6 pt-3 pb-6 flex-1">
              <SponsorAgreementsSection 
                sponsorId={sponsorId} 
                query={agreementsQuery} 
                statusFilter={agreementStatusFilter}
                setStatusFilter={setAgreementStatusFilter}
              />
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm rounded-2xl overflow-hidden bg-white flex flex-col h-full">
            <CardHeader className="px-6 py-4">
              <SectionHeader 
                icon={MessageSquare} 
                title="Protokoll" 
                description="Historie der Gespräche und Interaktionen."
                rightElement={
                  <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                    <Button
                      onClick={() => setIsLogDialogOpen(true)}
                      size="sm"
                      className="rounded-xl h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all font-bold flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Neu</span>
                    </Button>
                  </PermissionFeatureWrapper>
                }
              />
            </CardHeader>
            <CardContent className="px-6 pt-3 pb-6 flex-1 flex flex-col gap-4">
              <SponsorConversationLogSection sponsorId={sponsorId} />
              {logs.length > 0 && (
                <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsLogDialogOpen(true)}
                    className="w-full text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all rounded-xl"
                  >
                    Vollständiges Protokoll anzeigen
                  </Button>
                </PermissionFeatureWrapper>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Kontakte Sektion */}
        <Card className="border-none shadow-sm rounded-2xl overflow-hidden bg-white">
          <CardHeader className="px-6 py-4">
            <SectionHeader 
              icon={Users} 
              title="Ansprechpartner" 
              description="Verwalten Sie die Kontaktpersonen dieses Sponsors."
              rightElement={
                <>
                  <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                    <Button 
                      size="sm" 
                      onClick={() => setIsAddContactDialogOpen(true)}
                      className="rounded-xl h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all font-bold flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Hinzufügen</span>
                    </Button>
                  </PermissionFeatureWrapper>
                  <AddContactDialog
                    sponsorId={sponsorId}
                    existingContacts={contacts as any}
                    open={isAddContactDialogOpen}
                    onOpenChange={setIsAddContactDialogOpen}
                    onSuccess={() => sponsorQuery.refetch()}
                  />
                </>
              }
            />
          </CardHeader>
          <CardContent className="px-6 pt-3 pb-6">
            {contacts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {contacts.map((c: any, idx: number) => (
                  <ContactCard 
                    key={idx} 
                    contact={c} 
                    index={idx} 
                    sponsorId={sponsorId}
                    onDelete={() => handleContactDelete(idx)}
                    onUpdate={() => sponsorQuery.refetch()}
                    uploadMutation={uploadContactAvatarMutation}
                    contacts={contacts}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900">Keine Kontakte</h3>
                <p className="text-xs text-gray-500 mt-1 max-w-[200px]">
                  Fügen Sie Ansprechpartner hinzu, um die Kommunikation zu erleichtern.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <CreateSponsorAgreementDialog
        open={isCreateAgreementDialogOpen}
        onOpenChange={setIsCreateAgreementDialogOpen}
        sponsorId={sponsorId}
        onCreated={() => {
          agreementsQuery.refetch();
          setIsCreateAgreementDialogOpen(false);
        }}
      />
      <SponsorConversationLogDialog
        open={isLogDialogOpen}
        onOpenChange={setIsLogDialogOpen}
        sponsorId={sponsorId}
      />
    </SubPageLayout>
  );
};

// --- Helper Components ---

const KpiItem = ({ 
  label, 
  value, 
  icon: Icon, 
  highlight = false,
  variant = 'default'
}: { 
  label: string; 
  value: string | number; 
  icon: any; 
  highlight?: boolean;
  variant?: 'default' | 'success' | 'warning';
}) => (
  <div className="flex flex-col gap-1 min-w-0">
    <div className="flex items-center gap-1.5 text-gray-400">
      <Icon className="h-3.5 w-3.5 flex-shrink-0" />
      <p className="text-[10px] font-semibold uppercase tracking-wider truncate">{label}</p>
    </div>
    <p className={cn(
      "text-sm sm:text-base font-bold truncate",
      highlight ? "text-gray-900" : "text-gray-700",
      variant === 'success' && "text-emerald-600",
      variant === 'warning' && "text-amber-600",
    )}>
      {value}
    </p>
  </div>
);

const ContactCard = ({ 
  contact, 
  index, 
  sponsorId, 
  onDelete, 
  onUpdate, 
  uploadMutation,
  contacts 
}: { 
  contact: any; 
  index: number; 
  sponsorId: string; 
  onDelete: () => void; 
  onUpdate: () => void;
  uploadMutation: any;
  contacts: any[];
}) => {
  const [isEditContactDialogOpen, setIsEditContactDialogOpen] = useState(false);

  return (
    <div className="group relative min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-white p-4 transition-colors hover:border-gray-200">
      <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
        <div className="absolute right-2 top-2 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-card-actions-trigger="true"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg p-0 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditContactDialogOpen(true);
                }}
                className="cursor-pointer"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Bearbeiten
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.onchange = async () => {
                    const file = input.files?.[0];
                    if (!file) return;
                    const contactId = contact._id;
                    if (!contactId) return;
                    try {
                      await uploadMutation.mutateAsync({ id: sponsorId, contactId, file });
                      onUpdate();
                    } catch {}
                  };
                  input.click();
                }}
              >
                <Camera className="mr-2 h-4 w-4" />
                Bild hochladen
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PermissionFeatureWrapper>

      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
          {contact.avatarUrl ? (
            <img src={contact.avatarUrl} alt={contact.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-50">
              <User className="h-5 w-5 text-gray-300" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1 pr-9">
          <p className="text-sm font-semibold text-gray-900 truncate">{contact.name || "Unbekannt"}</p>
          <p className="mt-0.5 truncate text-[11px] text-gray-500">{contact.role || "Ansprechpartner"}</p>
          
          <div className="mt-2 space-y-1.5">
            {contact.email && (
              <p className="truncate text-[11px] text-gray-400">
                {contact.email}
              </p>
            )}
            {contact.phone && (
              <p className="truncate text-[11px] text-gray-400">
                {contact.phone}
              </p>
            )}
          </div>
        </div>
      </div>
      <EditContactDialog
        sponsorId={sponsorId}
        existingContacts={contacts}
        contactIndex={index}
        open={isEditContactDialogOpen}
        onOpenChange={setIsEditContactDialogOpen}
        onSuccess={onUpdate}
      />
    </div>
  );
};

export default SponsorDetailsPage;
